import type { CountryOption } from '@sentra/contracts';
import { countryOptions, normalize } from '@sentra/contracts';
import { Check, ChevronDown } from 'lucide-react';
import { type ReactNode, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/design/components/input';
import { cn } from '@/design/utils';
import { useCombobox } from '@/features/persons/form/combobox';
import { useLocale } from '@/i18n';

/**
 * The country field: two hundred and fifty options, searchable, named in the operator's language.
 *
 * **The names come from the platform, not from a translated list.** `countryOptions` resolves them
 * through `Intl.DisplayNames` and sorts them with this product's one shared Spanish collator, so
 * `Åland` sorts where a Spanish reader expects and nobody maintains five hundred strings. The codes
 * are static data in `@sentra/contracts`; only the names are resolved.
 *
 * **The search folds accents and case, and it is the SAME fold the register's search uses.**
 * `normalize` is what the blind indexes and the duplicate guard are computed over, so typing
 * `republica` finds `República Dominicana` here for exactly the reason it finds a person there. A
 * bespoke `toLowerCase()` comparison would differ from it the first time somebody typed an accent.
 *
 * **A native `<select>` is the right control everywhere else in this form and is the wrong one
 * here.** Two hundred and fifty options cannot be scanned, and a platform select's type-ahead
 * matches a prefix of the name only — so an operator looking for "Reino Unido" under `U` finds
 * nothing. This is a combobox with the ARIA the pattern requires, and the interaction it needs is
 * `useCombobox`, which the telephone prefix picker shares: there is one keyboard contract in this
 * form and one place it is written down.
 */
export function CountryCombobox({
  id,
  value,
  onChange,
  invalid,
  describedBy,
}: Readonly<{
  id: string;
  /** The stored ISO 3166-1 alpha-2 code, or the empty string for no country. */
  value: string;
  onChange: (next: string) => void;
  invalid: boolean;
  describedBy?: string;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const listId = useId();

  const options = useMemo(() => countryOptions(locale), [locale]);
  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const combobox = useCombobox(options, matching, (option) => {
    onChange(option.value);
  });

  // `null` means "showing the selection"; a string means the operator is typing over it. Without
  // the distinction, clearing the box would read as clearing the country, and selecting one would
  // have to overwrite whatever they were part-way through typing.
  const text = combobox.query ?? selected?.label ?? '';
  const highlighted = combobox.highlighted;

  return (
    <div className="relative">
      <Input
        id={id}
        role="combobox"
        type="text"
        autoComplete="off"
        aria-expanded={combobox.open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={invalid}
        {...(combobox.open && highlighted !== null
          ? { 'aria-activedescendant': `${listId}-${highlighted.value}` }
          : {})}
        {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
        value={text}
        onChange={(event) => {
          combobox.onQueryChange(event.target.value);
        }}
        onFocus={combobox.onFocus}
        onBlur={combobox.onBlur}
        onKeyDown={combobox.onKeyDown}
      />
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
      />

      {combobox.open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('person.form.countryList')}
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {combobox.matches.length === 0 && (
            // **`role="presentation"`, because a `listbox` may contain only options and groups.**
            // A bare `li` here fails `aria-required-children`, and it is invisible to the a11y
            // sweep because it appears only when a typed country matches nothing. The message is
            // still announced: the empty listbox is described by its own label, and the text is
            // read as the content of a presentational row rather than offered as a choice nobody
            // can make.
            <li role="presentation" className="px-2 py-1.5 text-sm text-muted-foreground">
              {t('person.form.countryNoMatches')}
            </li>
          )}
          {combobox.matches.map((option) => (
            // The option IS the `li`. A focusable element inside it would make the listbox's
            // children something other than options, which fails `aria-required-children` and is
            // also how the list ended up reachable only by tabbing INTO it.
            <li
              key={option.value}
              id={`${listId}-${option.value}`}
              role="option"
              aria-selected={option.value === value}
              // `onMouseDown` rather than `onClick`: the input's blur fires first otherwise, the
              // list unmounts, and the click lands on nothing.
              onMouseDown={(event) => {
                event.preventDefault();
                combobox.choose(option);
              }}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                option.value === highlighted?.value && 'bg-accent text-accent-foreground',
              )}
            >
              <Check
                aria-hidden
                className={cn('size-4', option.value === value ? 'opacity-100' : 'opacity-0')}
              />
              <span>{option.label}</span>
              <span className="ml-auto font-mono text-xs text-muted-foreground" data-numeric>
                {option.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** How many options the list offers at once, so an unfiltered open is not a wall of two hundred. */
const VISIBLE_MATCHES = 50;

/**
 * The options matching what has been typed, accent- and case-folded.
 *
 * A country is matched on its NAME or on its CODE, because an operator holding a passport is often
 * reading `MAR` off it rather than the word. Both sides are folded through `normalize`, which is the
 * same fold the register's own search and every blind index use.
 *
 * Declared at module level so `useCombobox`'s memo has a stable dependency rather than a function
 * rebuilt on every render.
 */
function matching(
  options: readonly CountryOption[],
  query: string | null,
): readonly CountryOption[] {
  if (query === null || query.trim().length === 0) {
    return options.slice(0, VISIBLE_MATCHES);
  }
  const needle = normalize(query);
  return options
    .filter(
      (option) =>
        normalize(option.label).includes(needle) || normalize(option.value).startsWith(needle),
    )
    .slice(0, VISIBLE_MATCHES);
}
